import { sql } from "drizzle-orm";
import { pgTable, text, varchar, integer, timestamp, boolean, real, jsonb, json, index, uniqueIndex, check } from "drizzle-orm/pg-core";
import { createInsertSchema } from "drizzle-zod";
import { z } from "zod";
import type { VerifiedPatternLensEvidence } from "./pattern-lenses";

export const PATTERN_MARK_VALUES = ["change", "sit"] as const;
export type PatternMarkValue = typeof PATTERN_MARK_VALUES[number];
export const PATTERN_MARK_CONFIDENCE = 50;

export const users = pgTable("users", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  email: text("email").notNull().unique(),
  passwordHash: text("password_hash"),
  displayName: text("display_name"),
  profilePicture: text("profile_picture"),
  timezone: text("timezone"),
  resetToken: text("reset_token"),
  resetTokenExpires: timestamp("reset_token_expires"),
  failedLoginAttempts: integer("failed_login_attempts").notNull().default(0),
  lockoutUntil: timestamp("lockout_until"),
  isAdmin: boolean("is_admin").notNull().default(false),
  dailyMessageCount: integer("daily_message_count").notNull().default(0),
  messageCountResetDate: text("message_count_reset_date"),
  firstLimitHitDate: text("first_limit_hit_date"),
  createdAt: timestamp("created_at").default(sql`NOW()`).notNull(),
});

export const userProfiles = pgTable("user_profiles", {
  id: integer("id").primaryKey().generatedAlwaysAsIdentity(),
  userId: varchar("user_id").notNull().unique().references(() => users.id, { onDelete: "cascade" }),
  answers: jsonb("answers").default(sql`'[]'::jsonb`).notNull(),
  onboardingComplete: boolean("onboarding_complete").notNull().default(false),
  onboardingStep: integer("onboarding_step").notNull().default(0),
  tutorialComplete: boolean("tutorial_complete").notNull().default(false),
  directAddressed: boolean("direct_addressed").notNull().default(false),
  createdAt: timestamp("created_at").default(sql`NOW()`).notNull(),
});

export const logs = pgTable("logs", {
  id: integer("id").primaryKey().generatedAlwaysAsIdentity(),
  userId: varchar("user_id").notNull().references(() => users.id, { onDelete: "cascade" }),
  mood: text("mood").notNull(),
  moodIntensity: real("mood_intensity").notNull().default(0.7),
  secondaryMood: text("secondary_mood"),
  energyLevel: integer("energy_level"),
  sleepQuality: integer("sleep_quality"),
  tags: text("tags").array().notNull().default(sql`'{}'::text[]`),
  moment: text("moment").notNull().default(""),
  timeOfDay: text("time_of_day"),
  source: text("source").notNull().default("manual"),
  summary: text("summary"),
  takeaways: text("takeaways").array().default(sql`'{}'::text[]`),
  impactAreas: jsonb("impact_areas").default(sql`'[]'::jsonb`),
  solutionsDiscussed: text("solutions_discussed").array().default(sql`'{}'::text[]`),
  reflectionQuote: text("reflection_quote"),
  themes: text("themes").array().default(sql`'{}'::text[]`),
  keyWords: text("key_words").array().default(sql`'{}'::text[]`),
  photos: text("photos").array().default(sql`'{}'::text[]`),
  directAddressed: boolean("direct_addressed").notNull().default(false),
  loggedAt: timestamp("logged_at").default(sql`NOW()`).notNull(),
}, (table) => [
  index("idx_logs_user_id").on(table.userId),
  index("idx_logs_user_id_logged_at").on(table.userId, table.loggedAt),
]);

export const patternCards = pgTable("pattern_cards", {
  id: integer("id").primaryKey().generatedAlwaysAsIdentity(),
  userId: varchar("user_id").notNull().references(() => users.id, { onDelete: "cascade" }),
  headline: text("headline").notNull(),
  confidence: integer("confidence").notNull(),
  dataPoints: text("data_points").array().notNull().default(sql`'{}'::text[]`),
  insight: text("insight").notNull(),
  narrative: text("narrative"),
  contributingFactors: text("contributing_factors").array().default(sql`'{}'::text[]`),
  timespan: text("timespan"),
  recommendation: text("recommendation"),
  patternType: text("pattern_type").notNull().default("general"),
  lensEvidence: jsonb("lens_evidence").$type<VerifiedPatternLensEvidence | null>(),
  lensQualified: boolean("lens_qualified").notNull().default(false),
  directAddressed: boolean("direct_addressed").notNull().default(false),
  severity: text("severity").default("moderate"),
  isArchived: boolean("is_archived").notNull().default(false),
  generatedAt: timestamp("generated_at").default(sql`NOW()`).notNull(),
}, (table) => [
  index("idx_pattern_cards_user_id").on(table.userId),
]);

export const patternMarks = pgTable("pattern_marks", {
  id: integer("id").primaryKey().generatedAlwaysAsIdentity(),
  userId: varchar("user_id").notNull().references(() => users.id, { onDelete: "cascade" }),
  patternCardId: integer("pattern_card_id").notNull().references(() => patternCards.id, { onDelete: "cascade" }),
  mark: text("mark").$type<PatternMarkValue>().notNull(),
  markedAt: timestamp("marked_at").default(sql`NOW()`).notNull(),
  baselineCount: integer("baseline_count").notNull(),
  resolvedAt: timestamp("resolved_at"),
  absenceAcknowledgedAt: timestamp("absence_acknowledged_at"),
}, (table) => [
  uniqueIndex("pattern_marks_user_card_unique").on(table.userId, table.patternCardId),
  check("pattern_marks_mark_check", sql`${table.mark} IN ('change', 'sit')`),
]);

export const chatMessages = pgTable("chat_messages", {
  id: integer("id").primaryKey().generatedAlwaysAsIdentity(),
  userId: varchar("user_id").notNull().references(() => users.id, { onDelete: "cascade" }),
  role: text("role").notNull(),
  content: text("content").notNull(),
  images: text("images").array().default(sql`'{}'`),
  directAddressed: boolean("direct_addressed").notNull().default(false),
  createdAt: timestamp("created_at").default(sql`NOW()`).notNull(),
}, (table) => [
  index("idx_chat_messages_user_id").on(table.userId),
  index("idx_chat_messages_user_id_created_at").on(table.userId, table.createdAt),
]);

export const dailySummaries = pgTable("daily_summaries", {
  id: integer("id").primaryKey().generatedAlwaysAsIdentity(),
  userId: varchar("user_id").notNull().references(() => users.id, { onDelete: "cascade" }),
  date: text("date").notNull(),
  mood: text("mood").notNull(),
  moodScore: real("mood_score").notNull(),
  energyAvg: real("energy_avg"),
  entryCount: integer("entry_count").notNull().default(0),
  headline: text("headline").notNull(),
  narrative: text("narrative").notNull(),
  wins: text("wins").array().default(sql`'{}'::text[]`),
  tensions: text("tensions").array().default(sql`'{}'::text[]`),
  themes: text("themes").array().default(sql`'{}'::text[]`),
  unansweredQuestions: text("unanswered_questions").array().default(sql`'{}'::text[]`),
  closingReflection: text("closing_reflection"),
  dominantTags: text("dominant_tags").array().default(sql`'{}'::text[]`),
  directAddressed: boolean("direct_addressed").notNull().default(false),
  generatedAt: timestamp("generated_at").default(sql`NOW()`).notNull(),
}, (table) => [
  index("idx_daily_summaries_user_id").on(table.userId),
]);

export const cachedProfiles = pgTable("cached_profiles", {
  id: integer("id").primaryKey().generatedAlwaysAsIdentity(),
  userId: varchar("user_id").notNull().unique().references(() => users.id, { onDelete: "cascade" }),
  profileData: jsonb("profile_data").notNull(),
  logCount: integer("log_count").notNull(),
  directAddressed: boolean("direct_addressed").notNull().default(false),
  generatedAt: timestamp("generated_at").default(sql`NOW()`).notNull(),
});

export const insertUserSchema = createInsertSchema(users).omit({ id: true, createdAt: true });
export const insertLogSchema = createInsertSchema(logs).omit({ loggedAt: true });
export const insertPatternCardSchema = createInsertSchema(patternCards).omit({ generatedAt: true });
export const insertUserProfileSchema = createInsertSchema(userProfiles).omit({ createdAt: true });
export const insertChatMessageSchema = createInsertSchema(chatMessages).omit({ createdAt: true });
export const insertDailySummarySchema = createInsertSchema(dailySummaries).omit({ generatedAt: true });

export type InsertUser = z.infer<typeof insertUserSchema>;
export type User = typeof users.$inferSelect;
export type InsertLog = z.infer<typeof insertLogSchema>;
export type Log = typeof logs.$inferSelect;
export type InsertPatternCard = typeof patternCards.$inferInsert;
export type PatternCard = typeof patternCards.$inferSelect;
export type PatternMark = typeof patternMarks.$inferSelect;
export type InsertPatternMark = typeof patternMarks.$inferInsert;
export type InsertUserProfile = z.infer<typeof insertUserProfileSchema>;
export type UserProfile = typeof userProfiles.$inferSelect;
export type InsertChatMessage = z.infer<typeof insertChatMessageSchema>;
export type ChatMessage = typeof chatMessages.$inferSelect;
export type InsertDailySummary = z.infer<typeof insertDailySummarySchema>;
export type DailySummary = typeof dailySummaries.$inferSelect;

export const MOODS = ["calm", "anxious", "energized", "drained", "creative", "overwhelmed"] as const;
export type Mood = typeof MOODS[number];

export const SECONDARY_MOODS = [
  "hopeful", "nostalgic", "restless", "grateful", "frustrated",
  "focused", "scattered", "numb", "tender", "defiant",
] as const;

export const CONTEXT_TAGS = ["Work", "Social", "Rest", "Creative", "Health", "Conflict"] as const;
export type ContextTag = typeof CONTEXT_TAGS[number];

export const TIME_OF_DAY = ["morning", "afternoon", "evening", "late night"] as const;

export const MOOD_COLORS: Record<Mood, string> = {
  calm: "#7EB8C9",
  anxious: "#C97E7E",
  energized: "#C9B87E",
  creative: "#A87EC9",
  drained: "#6B6870",
  overwhelmed: "#C9957E",
};

export const MOOD_DESCRIPTIONS: Record<Mood, string> = {
  calm: "Still, grounded, present",
  anxious: "Unsettled, bracing for something",
  energized: "Momentum, drive, forward",
  drained: "Depleted, running on empty",
  creative: "Fluid, connecting things",
  overwhelmed: "Too much, not enough time",
};

// Express session store (connect-pg-simple). Defined here so drizzle-kit
// push does not try to drop it as an "unknown" table.
export const session = pgTable("session", {
  sid: varchar("sid").primaryKey(),
  sess: json("sess").notNull(),
  expire: timestamp("expire", { precision: 6 }).notNull(),
}, (table) => [
  index("IDX_session_expire").on(table.expire),
]);
